import { v } from 'convex/values'

/**
 * The Templates world's taxonomy — the single source of truth shared between
 * the projection query, the browse surface and the column's tree.
 *
 * A category is NOT a table. Templates browses a projection over the two
 * primitives that already exist (`formTemplates`, live; `documentTemplates`,
 * dormant until now), and every category resolves to a filter over one of
 * them — or to nothing at all, which is what "Em breve" means. See
 * `docs/templates/README.md` for what exists, `spec.md` beside it for why.
 */
export const TEMPLATE_CATEGORIES = [
  'proposals',
  'contracts',
  'presentations',
  'briefings',
  'checklists',
  'sitework',
  'spreadsheets',
  'messages',
  'social',
] as const
export const templateCategory = v.union(
  ...TEMPLATE_CATEGORIES.map((category) => v.literal(category))
)
export type TemplateCategory = (typeof TEMPLATE_CATEGORIES)[number]

/**
 * Which launch-ready engine path renders a category, and therefore whether it
 * is usable from the Templates shelf yet.
 *
 * `null` is "Em breve", and it is a property of the CATEGORY rather than a
 * per-template flag someone remembers to set: the day a category's engine
 * lands, the badge leaves every one of its templates at once. It also means
 * no row can be marked coming-soon by accident, and none can be missed.
 *
 * Nothing gets an engine invented for it. A planilha de orçamento is columns,
 * formulas and tabs; the document engine is a block list against a fixed
 * design manifest and has no cell model. Forcing one in produces a worse
 * spreadsheet than the office already has AND a second layout engine to keep
 * forever.
 */
export const TEMPLATE_ENGINE: Record<TemplateCategory, 'document' | 'form' | null> = {
  briefings: 'form',
  checklists: 'form',
  contracts: null,
  messages: null,
  presentations: 'document',
  proposals: 'document',
  sitework: 'form',
  social: null,
  spreadsheets: null,
}

export const isComingSoon = (category: TemplateCategory): boolean =>
  TEMPLATE_ENGINE[category] === null

/**
 * How a category selects rows out of its engine's table.
 *
 * `obra` and `checklists` are the interesting pair: both are `checklist` form
 * templates, and what separates them is whether the template names a
 * construction PHASE. That is a real distinction and not an invented one — a
 * vistoria de fundação and a generic entrega checklist are different things to
 * an office — and deriving it costs no schema change.
 */
export const CATEGORY_SELECTOR: Record<
  TemplateCategory,
  | { engine: 'document'; kind: 'proposta' | 'apresentacao' | 'contrato' }
  | { engine: 'form'; category: 'briefing' | 'checklist'; phased?: boolean }
  | { engine: null }
> = {
  briefings: { category: 'briefing', engine: 'form' },
  checklists: { category: 'checklist', engine: 'form', phased: false },
  contracts: { engine: null },
  messages: { engine: null },
  presentations: { engine: 'document', kind: 'apresentacao' },
  proposals: { engine: 'document', kind: 'proposta' },
  sitework: { category: 'checklist', engine: 'form', phased: true },
  social: { engine: null },
  spreadsheets: { engine: null },
}

/**
 * What an "Em breve" category shows instead of rows.
 *
 * Named models rather than an empty shelf, because the whole decision behind
 * "Em breve" is that the shelf advertises where the product is going — and
 * "Planilhas (0)" advertises nothing. These live in code, not in the database:
 * they are a promise, and a promise with a row in a table is a thing someone
 * will eventually try to open.
 *
 * `blurb` is pt-BR here rather than a message id on purpose: these name
 * documents, which are content. See §5 for the promised-shelf behavior and
 * §7 for the locale rule in `docs/templates/spec.md`.
 */
export type ComingSoonTemplate = { blurb: string; key: string; name: string }

export const COMING_SOON_TEMPLATES: Record<TemplateCategory, ComingSoonTemplate[]> = {
  briefings: [],
  checklists: [],
  contracts: [
    {
      blurb: 'Partes, cláusulas e assinaturas para formalizar a prestação de serviços.',
      key: 'contract-services',
      name: 'Contrato de prestação de serviços',
    },
  ],
  messages: [
    {
      blurb: 'A mensagem que acompanha a proposta enviada.',
      key: 'message-proposal-sent',
      name: 'Envio de proposta',
    },
    {
      blurb: 'Retomada depois de alguns dias sem resposta.',
      key: 'message-follow-up',
      name: 'Follow-up de cliente',
    },
    {
      blurb: 'Confirmação e lembrete de visita agendada.',
      key: 'message-visit',
      name: 'Confirmação de visita',
    },
  ],
  presentations: [],
  proposals: [],
  sitework: [],
  social: [
    {
      blurb: 'Antes e depois do ambiente, em 9:16.',
      key: 'social-before-after',
      name: 'Antes e depois',
    },
    {
      blurb: 'Carrossel de apresentação do projeto entregue.',
      key: 'social-project-carousel',
      name: 'Projeto entregue',
    },
  ],
  spreadsheets: [
    {
      blurb: 'Ambientes, serviços e quantitativos com totais por etapa.',
      key: 'sheet-budget',
      name: 'Orçamento de obra',
    },
    {
      blurb: 'Cronograma físico-financeiro por etapa.',
      key: 'sheet-schedule',
      name: 'Cronograma',
    },
    {
      blurb: 'Lista de compras por ambiente, com códigos e fornecedores.',
      key: 'sheet-purchases',
      name: 'Lista de compras',
    },
  ],
}
